/* A church live event: the host's studio and the congregation's screen.
 *
 * ONE PAGE, TWO ROLES. Whoever runs the event sees their camera and the Go
 * live and End buttons; everybody else sees the stream. Both see the chat,
 * when the host allowed it, and the count of people watching.
 *
 * THE HOST'S CAMERA GOES OUT OVER HTTPS. The browser records it and sends it
 * a second at a time over a WebSocket to the API's own host — the only door
 * our servers have to the internet — and the server turns it into a stream
 * any phone or browser plays, six to ten seconds behind.
 *
 * A DROPPED CONNECTION IS NOT THE END. Church wifi drops mid-sermon; the
 * studio reconnects on its own and the service carries on. Only End ends it.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Hls from 'hls.js'
import {
  CalendarClock,
  Loader2,
  Radio,
  Send,
  Users,
  Video,
  VideoOff,
} from 'lucide-react'
import { api, ApiError, apiSocketUrl, apiUrl } from '@/api/client'
import { useEndLive, useFeatures, useLive } from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { cn } from '@/lib/utils'
import type { LiveChatMessage, LiveEvent } from '@/types/api'

export function LivePage() {
  const { callId } = useParams<{ callId: string }>()
  const navigate = useNavigate()
  const live = useLive(callId ?? null)
  const features = useFeatures()

  if (live.isLoading) {
    return (
      <Card className="p-s8 text-center">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-ink-3" aria-label="Loading" />
      </Card>
    )
  }
  if (live.isError || !live.data) {
    return (
      <Card className="p-s8 text-center">
        <p className="text-md font-bold">This live is not available</p>
        <p className="mt-1 text-base text-ink-2">
          It may have been removed, or it was not for you.
        </p>
        <Button variant="secondary" className="mt-s4" onClick={() => navigate('/')}>
          Back
        </Button>
      </Card>
    )
  }

  const event = live.data
  /* Watching stays open where going live is not offered; only the studio
     goes. */
  const hosting =
    event.may_host && event.state !== 'ended' && features.live_broadcasts

  return (
    <div className="space-y-s4">
      <Header event={event} />
      <div className="grid gap-s4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-s3">
          {hosting ? <Studio event={event} /> : <Screen event={event} />}
          {event.description ? (
            <Card className="p-s4 text-base text-ink-2">{event.description}</Card>
          ) : null}
        </div>
        {event.allow_chat ? (
          <Chat callId={event.id} open={event.state === 'live'} />
        ) : (
          <Card className="h-fit p-s4 text-sm text-ink-3">
            Chat is turned off for this live.
          </Card>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- header */

function Header({ event }: { event: LiveEvent }) {
  return (
    <div className="flex flex-wrap items-center gap-s3">
      <StateBadge state={event.state} />
      <h1 className="min-w-0 flex-1 truncate text-lg font-bold">{event.title}</h1>
      {event.state === 'live' ? (
        <span className="inline-flex items-center gap-1 text-sm text-ink-2">
          <Users className="h-4 w-4" aria-hidden />
          {event.watching} watching
        </span>
      ) : null}
    </div>
  )
}

function StateBadge({ state }: { state: LiveEvent['state'] }) {
  if (state === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-pill bg-danger px-s2 py-0.5 text-xs font-bold tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-pill bg-white" />
        LIVE
      </span>
    )
  }
  return (
    <span className="rounded-pill bg-surface-2 px-s2 py-0.5 text-xs font-bold tracking-wide text-ink-2">
      {state === 'ended' ? 'ENDED' : 'COMING UP'}
    </span>
  )
}

/* --------------------------------------------------------------- viewers */

/** What a member sees: the stream, or why there is not one yet. */
function Screen({ event }: { event: LiveEvent }) {
  /* THE FIRST SIGNED LINK IS KEPT. Every poll signs a fresh one; handing
     each to the player would restart the stream every four seconds. One
     lasts a whole service. */
  const [src, setSrc] = useState<string | null>(null)
  useEffect(() => {
    if (!src && event.stream_url) setSrc(apiUrl(event.stream_url))
  }, [event.stream_url, src])

  if (event.state === 'ended') return <Replay event={event} />

  if (event.state === 'scheduled' || !src) {
    return (
      <Stage>
        <div className="px-s6 text-center text-white">
          <CalendarClock className="mx-auto h-8 w-8 opacity-70" aria-hidden />
          <p className="mt-s2 text-md font-bold">
            {event.state === 'live'
              ? 'Waiting for the host to go on air…'
              : event.starts_at
                ? `Starts ${new Date(event.starts_at).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`
                : 'Starting soon'}
          </p>
          <p className="mt-1 text-sm opacity-70">
            It will appear here the moment it begins — no need to refresh.
          </p>
        </div>
      </Stage>
    )
  }

  return (
    <div className="relative">
      <HlsPlayer src={src} />
      {!event.on_air ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 m-s3 rounded bg-black/70 px-s3 py-s2 text-center text-sm text-white">
          The broadcast paused. It will carry on when the host is back.
        </div>
      ) : null}
    </div>
  )
}

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid aspect-video w-full place-items-center overflow-hidden rounded-lg bg-[#111]">
      {children}
    </div>
  )
}

/** The stream. Native HLS where the browser has it (Safari, iOS), hls.js
 *  everywhere else. */
function HlsPlayer({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [muted, setMuted] = useState(false)

  useEffect(() => {
    const video = ref.current
    if (!video) return
    let hls: Hls | null = null

    const start = () => {
      /* The click that opened this page usually lets it play with sound.
         When the browser says no, it plays muted with a button to unmute —
         a sermon that never starts is worse than one that starts quiet. */
      void video.play().catch(() => {
        video.muted = true
        setMuted(true)
        void video.play().catch(() => undefined)
      })
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src
      video.addEventListener('loadedmetadata', start, { once: true })
    } else if (Hls.isSupported()) {
      // Three segments behind the live edge: close to live, with enough in
      // hand to ride out a slow segment.
      hls = new Hls({ liveSyncDurationCount: 3 })
      hls.loadSource(src)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, start)
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return
        // A network blip or a stall at a restart: pick it up again rather
        // than leaving a frozen frame.
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError()
        else setTimeout(() => hls?.startLoad(), 2000)
      })
    }
    return () => {
      hls?.destroy()
    }
  }, [src])

  return (
    <div className="relative overflow-hidden rounded-lg bg-black">
      <video
        ref={ref}
        controls
        playsInline
        className="aspect-video w-full bg-black"
      />
      {muted ? (
        <button
          type="button"
          onClick={() => {
            if (ref.current) ref.current.muted = false
            setMuted(false)
          }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-pill bg-white px-s4 py-s2 text-base font-bold text-ink shadow"
        >
          Tap to turn the sound on
        </button>
      ) : null}
    </div>
  )
}

function Replay({ event }: { event: LiveEvent }) {
  const r = event.replay
  if (r?.status === 'ready' && r.url) {
    const ratio = r.width && r.height ? r.width / r.height : 16 / 9
    return (
      <video
        src={r.url}
        poster={r.poster_url ?? undefined}
        controls
        playsInline
        preload="metadata"
        style={{ aspectRatio: ratio }}
        className="max-h-[70vh] w-full rounded-lg bg-black"
      />
    )
  }
  return (
    <Stage>
      <div className="px-s6 text-center text-white">
        {r?.status === 'processing' ? (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin opacity-70" aria-hidden />
            <p className="mt-s2 text-md font-bold">The replay is being prepared</p>
            <p className="mt-1 text-sm opacity-70">
              It will play here as soon as it is ready.
            </p>
          </>
        ) : (
          <p className="text-md font-bold">This live has ended.</p>
        )}
      </div>
    </Stage>
  )
}

/* ---------------------------------------------------------------- studio */

type StudioState =
  | 'off'           // no camera yet
  | 'preview'       // camera on, not broadcasting
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'ending'
  | 'ended'
  | 'refused'       // not allowed, or the event has ended

/** What the browser can record in, best first. VP8/Opus in WebM is what
 *  Chrome and Firefox make; Safari makes MP4. The server takes any of them. */
function pickFormat(video: boolean): string | undefined {
  const options = video
    ? ['video/webm;codecs=vp8,opus', 'video/webm;codecs=h264,opus',
       'video/webm', 'video/mp4;codecs=avc1,mp4a', 'video/mp4']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return options.find((t) => MediaRecorder.isTypeSupported(t))
}

function Studio({ event }: { event: LiveEvent }) {
  const preview = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const socket = useRef<WebSocket | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  /* Whether the host WANTS to be live. A socket closing while this is true
     is a drop to recover from; while false, it is the end. */
  const wanted = useRef(false)
  const attempts = useRef(0)
  const [state, setState] = useState<StudioState>('off')
  const [problem, setProblem] = useState<string | null>(null)
  const [watching, setWatching] = useState(event.watching)
  const [since, setSince] = useState<number | null>(null)
  const [slow, setSlow] = useState(false)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const endLive = useEndLive(event.id)
  const [, tick] = useState(0)

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])

  // Everything stops when the page goes — the camera light included.
  useEffect(
    () => () => {
      wanted.current = false
      recorder.current?.state !== 'inactive' && recorder.current?.stop()
      socket.current?.close()
      stream.current?.getTracks().forEach((t) => t.stop())
    },
    [],
  )

  async function openCamera() {
    setProblem(null)
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch {
      try {
        // No camera, or not allowed one: a sermon can go out as sound.
        stream.current = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch {
        setProblem(
          'Your browser is not letting us use the camera or microphone. ' +
            'Allow them for this site, then try again.',
        )
        return
      }
    }
    if (preview.current) preview.current.srcObject = stream.current
    setState('preview')
  }

  const hasVideo = () => Boolean(stream.current?.getVideoTracks().length)

  async function connect() {
    const media = stream.current
    if (!media) return
    let ticket: string
    try {
      ticket = (await api.post<{ ticket: string }>(`/live/${event.id}/ticket`)).ticket
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 400)) {
        setState('refused')
        setProblem(err.message)
        return
      }
      return retry()
    }

    const ws = new WebSocket(
      apiSocketUrl(
        `/live/${event.id}/ingest?ticket=${encodeURIComponent(ticket)}` +
          (hasVideo() ? '' : '&audio_only=true'),
      ),
    )
    ws.binaryType = 'arraybuffer'
    socket.current = ws

    ws.onopen = () => {
      /* A NEW RECORDER FOR EVERY CONNECTION. A recording's first chunk
         carries its header; a reconnect fed the middle of an old recording
         would be unreadable. */
      const rec = new MediaRecorder(media, {
        mimeType: pickFormat(hasVideo()),
        videoBitsPerSecond: 2_500_000,
        audioBitsPerSecond: 128_000,
      })
      rec.ondataavailable = (e) => {
        if (ws.readyState !== WebSocket.OPEN || !e.data.size) return
        // More than a few seconds queued means the upload cannot keep up.
        setSlow(ws.bufferedAmount > 4 * 1024 * 1024)
        ws.send(e.data)
      }
      rec.start(1000)
      recorder.current = rec
    }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data as string) as { type: string; watching?: number }
        if (msg.type === 'on_air') {
          attempts.current = 0
          setState('live')
          setSince((s) => s ?? Date.now())
        }
        if (typeof msg.watching === 'number') setWatching(msg.watching)
        if (msg.type === 'ended') setState('ended')
      } catch {
        // Not ours to read.
      }
    }

    ws.onclose = (e) => {
      if (recorder.current?.state !== 'inactive') recorder.current?.stop()
      if (e.code === 4409) return setState('ended')
      if (e.code === 4403) {
        setState('refused')
        setProblem('Only whoever runs this live can broadcast it.')
        return
      }
      if (wanted.current) retry()
    }
  }

  function retry() {
    if (!wanted.current) return
    setState('reconnecting')
    attempts.current += 1
    // Quickly at first, then more patiently: a phone walking between rooms
    // is back in seconds, a router rebooting takes a minute.
    const delay = Math.min(10_000, 1000 * attempts.current)
    setTimeout(() => void connect(), delay)
  }

  async function goLive() {
    wanted.current = true
    setState('connecting')
    await connect()
  }

  async function end() {
    setConfirmEnd(false)
    wanted.current = false
    setState('ending')
    const ws = socket.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (recorder.current?.state !== 'inactive') recorder.current?.stop()
      ws.send(JSON.stringify({ type: 'end' }))
    } else {
      // The connection had already gone: end it the other way.
      endLive.mutate()
    }
    stream.current?.getTracks().forEach((t) => t.stop())
  }

  const elapsed = since ? Math.floor((Date.now() - since) / 1000) : 0
  const clock = `${Math.floor(elapsed / 3600) ? `${Math.floor(elapsed / 3600)}:` : ''}${String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`

  return (
    <div className="space-y-s3">
      <div className="relative overflow-hidden rounded-lg bg-[#111]">
        <video
          ref={preview}
          autoPlay
          muted
          playsInline
          className={cn('aspect-video w-full object-contain', state === 'off' && 'hidden')}
        />
        {state === 'off' ? (
          <Stage>
            <div className="px-s6 text-center text-white">
              <Video className="mx-auto h-8 w-8 opacity-70" aria-hidden />
              <p className="mt-s2 text-md font-bold">You are running this live</p>
              <p className="mt-1 text-sm opacity-70">
                Turn your camera on to check how you look before going live.
              </p>
            </div>
          </Stage>
        ) : null}
        {state !== 'off' && !hasVideo() ? (
          <div className="absolute inset-0 grid place-items-center text-white">
            <div className="text-center">
              <VideoOff className="mx-auto h-8 w-8 opacity-70" aria-hidden />
              <p className="mt-s2 text-sm">Sound only — no camera</p>
            </div>
          </div>
        ) : null}
        {state === 'live' ? (
          <div className="absolute left-s3 top-s3 flex items-center gap-s2">
            <span className="rounded-pill bg-danger px-s2 py-0.5 text-xs font-bold text-white">
              LIVE {clock}
            </span>
            <span className="rounded-pill bg-black/60 px-s2 py-0.5 text-xs font-semibold text-white">
              {watching} watching
            </span>
          </div>
        ) : null}
      </div>

      {problem ? (
        <p className="rounded bg-danger-soft px-s3 py-s2 text-sm text-danger">{problem}</p>
      ) : null}
      {state === 'reconnecting' ? (
        <p className="rounded bg-warning-soft px-s3 py-s2 text-sm text-warning-ink">
          The connection dropped — reconnecting. Viewers see a pause, not the end.
        </p>
      ) : null}
      {slow && state === 'live' ? (
        <p className="rounded bg-warning-soft px-s3 py-s2 text-sm text-warning-ink">
          Your internet is struggling to keep up. Viewers may see pauses — a
          wired connection or better signal helps.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-s2">
        {state === 'off' ? (
          <Button variant="primary" onClick={openCamera}>
            <Video className="h-4 w-4" aria-hidden /> Turn camera on
          </Button>
        ) : null}
        {state === 'preview' ? (
          <Button variant="danger" onClick={goLive}>
            <Radio className="h-4 w-4" aria-hidden /> Go live
          </Button>
        ) : null}
        {state === 'connecting' ? (
          <Button variant="danger" disabled>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Going live…
          </Button>
        ) : null}
        {state === 'live' || state === 'reconnecting' ? (
          <Button variant="secondary" onClick={() => setConfirmEnd(true)}>
            End live
          </Button>
        ) : null}
        {state === 'ending' ? (
          <Button variant="secondary" disabled>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Ending…
          </Button>
        ) : null}
        {state === 'ended' ? (
          <p className="text-base text-ink-2">
            The live has ended. The replay will appear on this page shortly.
          </p>
        ) : null}
      </div>

      <Confirm
        open={confirmEnd}
        onOpenChange={setConfirmEnd}
        title="End this live for everybody?"
        body="Viewers see that it has ended, and the recording becomes the replay. This cannot be undone."
        confirmLabel="End it"
        onConfirm={end}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ chat */

function Chat({ callId, open }: { callId: string; open: boolean }) {
  const [messages, setMessages] = useState<LiveChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const last = useRef<string | null>(null)
  const list = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let stopped = false
    async function poll() {
      try {
        const q = last.current ? `?after=${last.current}` : ''
        const { items } = await api.get<{ items: LiveChatMessage[] }>(`/live/${callId}/chat${q}`)
        if (!stopped && items.length) {
          last.current = items[items.length - 1].id
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id))
            return [...prev, ...items.filter((m) => !seen.has(m.id))].slice(-300)
          })
        }
      } catch {
        // The next poll tries again.
      }
    }
    void poll()
    const t = setInterval(poll, 2500)
    return () => {
      stopped = true
      clearInterval(t)
    }
  }, [callId])

  // Follow the conversation, as long as the reader is at the bottom of it.
  useEffect(() => {
    const el = list.current
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])

  async function send() {
    const body = draft.trim()
    if (!body || sending) return
    setSending(true)
    try {
      const msg = await api.post<LiveChatMessage>(`/live/${callId}/chat`, { body })
      setDraft('')
      last.current = msg.id
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]))
    } finally {
      setSending(false)
    }
  }

  return (
    <Card className="flex h-[420px] flex-col lg:h-[calc(100vh-220px)]">
      <p className="border-b border-line-2 px-s4 py-s3 text-sm font-bold text-ink-2">
        Live chat
      </p>
      <div ref={list} className="flex-1 space-y-s3 overflow-y-auto px-s4 py-s3">
        {messages.length ? (
          messages.map((m) => (
            <div key={m.id} className="flex gap-s2">
              <Avatar name={m.name} src={m.avatar_url} size="sm" />
              <p className="min-w-0 text-sm">
                <span className="font-semibold">{m.name}</span>{' '}
                <span className="break-words text-ink-2">{m.body}</span>
              </p>
            </div>
          ))
        ) : (
          <p className="text-sm text-ink-3">
            {open ? 'Say hello — everyone watching can see it.' : 'The chat opens when the live begins.'}
          </p>
        )}
      </div>
      <form
        className="flex gap-s2 border-t border-line-2 p-s3"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={500}
          disabled={!open}
          placeholder={open ? 'Write a message…' : 'Chat is closed'}
          className="min-w-0 flex-1 rounded-pill border border-line bg-surface-2 px-s3 py-s2 text-sm outline-none focus:border-brand"
        />
        <Button type="submit" variant="primary" disabled={!open || !draft.trim() || sending} aria-label="Send">
          <Send className="h-4 w-4" aria-hidden />
        </Button>
      </form>
    </Card>
  )
}
